import { formatNumber } from "./format.js";

export function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

/** Nearest data-point index for a cursor at horizontal fraction fx (0..1) over a line chart. */
export function nearestIndex(count: number, fx: number): number {
  if (count <= 1) return 0;
  return clamp(Math.round(clamp(fx, 0, 1) * (count - 1)), 0, count - 1);
}

/** Bar-slot index for a cursor at horizontal fraction fx (0..1) over count equal slots. */
export function slotIndex(count: number, fx: number): number {
  if (count <= 0) return 0;
  return clamp(Math.floor(clamp(fx, 0, 1) * count), 0, count - 1);
}

const BINARY_UNITS = ["B", "KiB", "MiB", "GiB", "TiB"];

export function formatBytesBinary(value: number): string {
  if (value === 0) return "0 B";
  const i = clamp(Math.floor(Math.log(Math.abs(value)) / Math.log(1024)), 0, BINARY_UNITS.length - 1);
  const scaled = value / 1024 ** i;
  return `${Math.abs(scaled) >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)} ${BINARY_UNITS[i]}`;
}

/** CloudWatch-style unit humanizer for chart tooltips and axis ticks. */
export function humanizeUnitValue(value: number, unit: string): string {
  switch (unit) {
    case "Percent":
      return `${value.toFixed(1)}%`;
    case "Bytes":
      return formatBytesBinary(value);
    case "Seconds":
      return `${(value * 1000).toFixed(0)} ms`;
    case "Milliseconds":
      return `${value.toFixed(0)} ms`;
    default:
      return formatNumber(value);
  }
}

export type BudgetTone = "accent" | "warn" | "alarm";

export interface BudgetGeometry {
  trackMax: number;
  actualFrac: number;
  limitFrac: number;
  forecastFrac: number | null;
  overAmount: number;
  overPct: number;
  tone: BudgetTone;
}

export function budgetGeometry(actual: number, forecast: number | null, limit: number): BudgetGeometry {
  const trackMax = Math.max(limit, forecast ?? 0, actual, 0) * 1.02 || 1;
  const frac = (v: number) => clamp(v / trackMax, 0, 1);
  let tone: BudgetTone;
  if (limit > 0) {
    tone = actual >= limit ? "alarm" : actual >= limit * 0.9 ? "warn" : "accent";
  } else {
    tone = actual > 0 ? "alarm" : "accent";
  }
  const worst = Math.max(actual, forecast ?? 0);
  const overAmount = Math.max(0, worst - limit);
  const overPct = limit > 0 ? (overAmount / limit) * 100 : 0;
  return {
    trackMax,
    actualFrac: frac(actual),
    limitFrac: frac(limit),
    forecastFrac: forecast === null ? null : frac(forecast),
    overAmount,
    overPct,
    tone,
  };
}
